import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// type sent to /api/azure/recommendations, keyed by sub-tab index
const TAB_TYPES = ['COMBINED_VM_SCALE_SET', 'CUSTOM_VM_SCALE_SET', 'VM_SCALE_SET'];

// ── Rec helpers ──────────────────────────────────────────────

const resolveVmssRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE', 'DEALLOCATE'].includes(t)) recPillClass = 'rec-id';
  else if (['RIGHTSIZE', 'DOWNGRADE', 'LOWER FLOOR', 'LOWERFLOOR', 'REDUCE'].includes(t)) recPillClass = 'rec-rs';
  else if (['SCHEDULE', 'SCHEDULESCALEIN'].includes(t)) recPillClass = 'rec-warn';
  return { recType: t || 'OPTIMAL', recPillClass };
};

const notNA = (v) => (v && String(v).trim().toUpperCase() !== 'NA') ? v : null;

// ── Row Mapper ───────────────────────────────────────────────
// All three tabs (COMBINED / CUSTOM / VM_SCALE_SET) share one item shape; the
// few source-specific keys (impact, avgMemoryUsage, remark…) are read defensively.

const toVmssRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? item.recommendationSubCategory ?? (saving > 0 ? 'RIGHTSIZE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveVmssRec(rawRec);

  // Show the backend's recommendation detail exactly; when it is "NA" (or absent)
  // fall through to the other detail fields, and finally show "—".
  const remark =
    notNA(item.recommendationDetail) ??
    notNA(item.remark) ??
    notNA(item.recommendationSubCategory) ??
    '—';

  const currentSku    = item.skuName ?? item.currentSku ?? '—';
  const recommendedSku = item.recommendedSku ?? '—';

  return {
    id: item.id,
    name: item.name ?? item.vmssName ?? '—',
    location: item.location ?? item.resourceLocation ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    osType: item.osType ?? '—',
    currentSku,
    recommendedSku,
    skuChange: currentSku !== '—' && recommendedSku !== '—' && currentSku !== recommendedSku,
    skuTier: item.skuTier ?? '—',
    impact: item.impact ?? '—',
    currentInstances: item.currentInstanceCount ?? null,
    minInstances: item.minInstanceCount ?? null,
    maxInstances: item.maxInstanceCount ?? null,
    defaultInstances: item.defaultInstanceCount ?? null,
    avgCpu: item.averageCpuValue ?? item.avgCpuUsage ?? null,
    peakCpu: item.peakCpuValue ?? null,
    memory: item.avgMemoryUsage ?? null,
    status: item.vmssStatus ?? item.provisioningState ?? '—',
    timeCreated: item.timeCreated ?? null,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    savingValue: saving,
    recommendedBy: item.recommendedBy ?? '—',
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data) => (data ?? []).map(toVmssRow);

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec     = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving  = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const runningSets  = rows.filter((r) => (Number(r.currentInstances) || 0) > 0).length;
  const stoppedSets  = rows.length - runningSets;
  const rightsize    = rows.filter((r) => r.skuChange).length;

  // 4th card left to the helper default: "Total Payable (after savings)"
  return buildBaseCostKpis({
    countTitle: 'Scale Sets',
    countValue: rows.length,
    countSubtitle: `${runningSets} running · ${stoppedSets} stopped · ${rightsize} to rightsize`,
    current, saving, recommended,
  });
};

// ── Charts ────────────────────────────────────────────────────

// Capacity vs CPU trend — driven by VM_SCALE_SET_TREND API
// ([{ date, avgCpu, totalInstances }]).
const buildTrendLine = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  return {
    labels: arr.map((d) => new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Avg CPU %',
        data: arr.map((d) => parseFloat(Number(d.avgCpu ?? 0).toFixed(1))),
        yAxisID: 'y1',
        borderColor: '#f57c00',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        borderDash: [4, 3],
      },
      {
        label: 'Instances',
        data: arr.map((d) => Number(d.totalInstances ?? 0)),
        yAxisID: 'y2',
        borderColor: '#0078d4',
        backgroundColor: 'rgba(0,120,212,0.15)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
      },
    ],
  };
};

const buildCostByVmSize = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const map = {};
  rows.forEach((r) => {
    const sz = r.currentSku && r.currentSku !== '—' ? r.currentSku : null;
    if (!sz) return;
    map[sz] = (map[sz] || 0) + (hasCost ? r.costValue : 1);
  });
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.length) return null;
  return {
    labels: sorted.map(([k]) => k),
    datasets: [{
      label: hasCost ? 'Cost ($)' : 'Scale sets',
      data: sorted.map(([, v]) => parseFloat(v.toFixed(2))),
      backgroundColor: '#0078d4',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

// Autoscale floor (minInstanceCount) vs the demand the avg CPU actually justifies.
const buildFloorVsDemand = (rows) => {
  const named = rows.filter((r) => r.name && r.name !== '—' && r.currentInstances != null).slice(0, 6);
  if (!named.length) return null;
  return {
    labels: named.map((r) => (r.name.length > 16 ? r.name.slice(0, 15) + '…' : r.name)),
    datasets: [
      {
        label: 'Min floor set',
        data: named.map((r) => Number(r.minInstances ?? r.currentInstances) || 0),
        backgroundColor: '#f57c00',
        borderRadius: 4,
        barPercentage: 0.7,
      },
      {
        label: 'Avg demand',
        data: named.map((r) =>
          r.avgCpu != null ? Math.max(1, Math.round((Number(r.currentInstances) || 0) * Number(r.avgCpu) / 100)) : 0
        ),
        backgroundColor: '#2ea66f',
        borderRadius: 4,
        barPercentage: 0.7,
      },
    ],
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Rightsize SKU': 0, 'Lower autoscale floor': 0, 'Schedule off-hours': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (['SCHEDULE', 'SCHEDULESCALEIN'].includes(r.recType)) buckets['Schedule off-hours'] += s;
    else if (['LOWERFLOOR', 'LOWER FLOOR', 'REDUCE'].includes(r.recType)) buckets['Lower autoscale floor'] += s;
    else buckets['Rightsize SKU'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#f57c00', '#ef4444', '#2ea66f'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (['SCHEDULE', 'SCHEDULESCALEIN'].includes(r.recType)) { actionLabel = 'Schedule →'; actionClass = 'mini-btn ghost'; }
    else if (['RIGHTSIZE', 'DOWNGRADE'].includes(r.recType)) actionLabel = 'Rightsize →';
    else if (['LOWERFLOOR', 'LOWER FLOOR', 'REDUCE'].includes(r.recType)) actionLabel = 'Lower Floor →';
    return {
      id: i + 1,
      title: r.name || r.id,
      amount: r.saving,
      desc: `${r.currentSku} · ${r.currentInstances ?? '—'} inst · ${r.location}`,
      actionLabel,
      actionClass,
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── CPU Util Bar cell ─────────────────────────────────────────

const CpuUtilCell = ({ row }) => {
  if (row.avgCpu == null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  const pct  = Math.min(100, Math.max(0, Number(row.avgCpu)));
  const color = pct < 35 ? '#2ea66f' : pct < 70 ? '#f57c00' : '#ef4444';
  const title = row.peakCpu != null ? `Peak ${Number(row.peakCpu).toFixed(1)}%` : undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }} title={title}>
      <div style={{ flex: 1, height: 6, background: '#eef1f7', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{pct.toFixed(0)}%</span>
    </div>
  );
};

// ── Table Columns ─────────────────────────────────────────────

const vmssColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'name',          label: 'Scale Set Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'osType',        label: 'OS' },
  { key: 'currentSku',    label: 'Current SKU' },
  {
    key: 'recommendedSku', label: 'Recommended SKU',
    render: (row) => (
      <span style={row.skuChange ? { fontWeight: 600 } : undefined}>{row.recommendedSku}</span>
    ),
  },
  {
    key: 'currentInstances', label: 'Instances', className: 'num',
    render: (row) => row.currentInstances == null ? '—' : (
      <span title={`min ${row.minInstances ?? '—'} · default ${row.defaultInstances ?? '—'} · max ${row.maxInstances ?? '—'}`}>
        {row.currentInstances}
        {(row.minInstances != null && row.maxInstances != null) && (
          <span style={{ color: 'var(--muted)', fontSize: 11 }}> ({row.minInstances}–{row.maxInstances})</span>
        )}
      </span>
    ),
  },
  { key: 'avgCpu', label: 'Avg CPU', render: (row) => <CpuUtilCell row={row} /> },
  {
    key: 'memory', label: 'Avg Mem', className: 'num',
    render: (row) => row.memory != null ? `${Number(row.memory).toFixed(1)}%` : <span style={{ color: 'var(--muted)' }}>—</span>,
  },
  {
    key: 'status', label: 'Status',
    render: (row) => (
      <span className={`status-pill ${(row.status ?? '').toLowerCase() === 'running' ? 'st-running' : 'st-stopped'}`}>
        {row.status}
      </span>
    ),
  },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const VmScaleSetOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [trendData,       setTrendData]       = useState(null);
  const [isLoadingTrend,  setIsLoadingTrend]  = useState(false);
  const [vmSizeData,      setVmSizeData]      = useState(null);
  const [floorDemandData, setFloorDemandData] = useState(null);
  const [savingsByAction, setSavingsByAction] = useState(null);
  const [insightActions,  setInsightActions]  = useState([]);
  const [insightTotal,    setInsightTotal]    = useState(0);

  const [tagList,          setTagList]          = useState([]);
  const [filterList,       setFilterList]       = useState({ tag: '', values: [] });
  const [locationFilter,   setLocationFilter]   = useState('');
  const [vmSizeFilter,     setVmSizeFilter]     = useState('');
  const [statusFilter,     setStatusFilter]     = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      scaleSetId: row.id, scaleSetName: row.name, location: row.location,
      resourceGroup: row.resourceGroup, osType: row.osType,
      currentSku: row.currentSku, recommendedSku: row.recommendedSku,
      instances: row.currentInstances, minInstances: row.minInstances, maxInstances: row.maxInstances,
      status: row.status, avgCpu: row.avgCpu, peakCpu: row.peakCpu, avgMemory: row.memory,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the data provided, give a short and natural insight ' +
      'as if you are speaking directly to the user — not reading a report. ' +
      'Mention the cost situation, what savings are within reach, and what the user should consider doing. ' +
      'Keep it conversational, friendly, and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'VM_SCALE_SET', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  // Capacity/CPU trend is account-level (not per recommendation source) → fetched once per account.
  const fetchTrendData = (userId) => {
    setIsLoadingTrend(true);
    setTrendData(null);
    RecommendationApiService.getAzureData({ userId, type: 'VM_SCALE_SET_TREND', userAccountId: selectedAccount })
      .then((res) => setTrendData(buildTrendLine(res?.data)))
      .catch((err) => console.error('Error fetching VMSS trend', err))
      .finally(() => setIsLoadingTrend(false));
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId, isForceRefresh = false) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    const reqObj = { userId, type: TAB_TYPES[0], userAccountId: selectedAccount };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching combined VMSS recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId, isForceRefresh = false) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    const reqObj = {
      userId, type: TAB_TYPES[1], userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching custom VMSS recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId, isForceRefresh = false) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    const reqObj = { userId, type: TAB_TYPES[2], userAccountId: selectedAccount };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching azure VMSS recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  // Lazy fetch: only hits the API for a tab the first time it is opened.
  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) fetchCombinedData(userId, isForceRefresh);
    else if (tabIndex === 1) fetchCustomData(userId, isForceRefresh);
    else if (tabIndex === 2) fetchAzureData(userId, isForceRefresh);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    if (isLoadingActive) return;
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
    const userId = getUserId();
    if (userId) fetchTrendData(userId);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
      const userId = getUserId();
      if (userId) fetchTrendData(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // CloudsAnalytics (custom) tag filters are server-side — refetch only while its tab is active.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const locations = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const vmSizes   = useMemo(() => [...new Set(baseRows.map((r) => r.currentSku).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const statuses  = useMemo(() => [...new Set(baseRows.map((r) => r.status).filter((v) => v && v !== '—'))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    if (vmSizeFilter)   rows = rows.filter((r) => r.currentSku === vmSizeFilter);
    if (statusFilter)   rows = rows.filter((r) => (r.status ?? '').toLowerCase() === statusFilter.toLowerCase());
    return rows;
  }, [baseRows, locationFilter, vmSizeFilter, statusFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setVmSizeData(buildCostByVmSize(filteredRows));
      setFloorDemandData(buildFloorVsDemand(filteredRows));
      setSavingsByAction(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setVmSizeData(null); setFloorDemandData(null); setSavingsByAction(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with what's on screen
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'VM Scale Set Optimization',
      kpi: activeKpi,
      vmssRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, vmssColumns.filter((c) => c.key !== 'action'), `vm-scale-set-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setLocationFilter(''); setVmSizeFilter(''); setStatusFilter('');
    setFilterList({ tag: '', values: [] });
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>VM Scale Set</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            VM Scale Set Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} scale sets</span>}
          </h1>
          <div className="meta">Right-size instances, tune autoscale floors, and schedule capacity for scale sets</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Capacity vs CPU utilization — 30 days" subtitle="Instances scaled out vs CPU demand">
          <LineChart
            data={trendData}
            loading={isLoadingTrend}
            chartType="line"
            y1Label="Avg CPU %"
            y2Label="Instances"
            y2Prefix=""
            y1Max={100}
            xMaxRotation={0}
          />
        </Card>
        <Card className="first" title="Cost by VM size" subtitle="Scale set instance families">
          <HorizontalBarChart data={vmSizeData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top scale set actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="scale sets"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Sub-tabs */}
      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>VM Size</label>
          <select value={vmSizeFilter} onChange={(e) => setVmSizeFilter(e.target.value)}>
            <option value="">All sizes</option>
            {vmSizes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {(filterList.tag || locationFilter || vmSizeFilter || statusFilter) && (
          <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>
        )}
        <button className="btn ghost" onClick={handleRefresh} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <Table
        rows={activeRows}
        columns={vmssColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No VM scale sets found."
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Autoscale floor vs actual demand" subtitle="Min instances set vs instances actually needed">
          <GroupedBarChart data={floorDemandData} loading={isLoadingActive} ySuffix="" />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsByAction} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default VmScaleSetOptimization;
